import React, { useState, useEffect, useCallback } from "react";
import {
  FaCheckDouble,
  FaTrash,
  FaBullhorn,
  FaEnvelopeOpen,
  FaEnvelope,
  FaInbox,
  FaCheck,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useAuth } from "../context/authcontext";
import ConfirmModal from "../employee/delete_modal";
import Pagination from "../common/pagination";
import Dropdown from "../../components/modal/dropdown";
import api from "../services/api";
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
} from "../services/notificationservice";
import AnnouncementModal from "./announcement_modal";
import NotificationDetailModal from "./notification_viewpage";
import {
  TAB_OPTIONS as BASE_TAB_OPTIONS,
  TYPE_FILTER_OPTIONS,
  PRIORITY_FILTER_OPTIONS,
  formatRelativeTime,
  getPriorityBadgeClass,
  getTypeIcon,
} from "./notification_utils";
import "./notifications.css";

const PAGE_SIZE = 15;

// Tabs need their icons attached here (JSX), the rest of the label/value
// pairs live in notificationutils so they can be shared without importing
// react-icons into a plain data file.
const TAB_ICONS = {
  all: <FaInbox />,
  unread: <FaEnvelope />,
  read: <FaEnvelopeOpen />,
};
const TAB_OPTIONS = BASE_TAB_OPTIONS.map((t) => ({ ...t, icon: TAB_ICONS[t.value] }));

export default function NotificationsPage() {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("notification.create");

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);

  // Filters — mirrors the Employee page's filter state pattern
  const [activeTab, setActiveTab] = useState("all"); // 'all', 'unread', 'read'
  const [typeFilter, setTypeFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");

  // View section
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Announcement section
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
      };
      if (activeTab === "unread") params.is_read = false;
      if (activeTab === "read") params.is_read = true;
      if (typeFilter) params.notification_type = typeFilter;
      if (priorityFilter) params.priority = priorityFilter;

      const res = await getNotifications(params);
      const data = res?.data || res?.results || [];
      const count = res?.meta?.count || res?.count || data.length;

      setNotifications(Array.isArray(data) ? data : []);
      setTotalCount(count);
    } catch (err) {
      toast.error("Failed to load notifications");
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [page, activeTab, typeFilter, priorityFilter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, typeFilter, priorityFilter]);

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
      toast.success("Notification marked as read");
    } catch {
      toast.error("Failed to mark notification as read");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success("All notifications marked as read");
    } catch {
      toast.error("Failed to mark all notifications as read");
    }
  };

  const handleDeleteClick = (n, e) => {
    e.stopPropagation();
    setDeleteTarget(n);
  };

  const confirmDeleteNotification = async () => {
    if (!deleteTarget) return;
    try {
      await deleteNotification(deleteTarget.id);
      toast.success("Notification deleted successfully");
      setDeleteTarget(null);
      fetchNotifications();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete notification");
    }
  };

  // Opens the view modal. List rows come from a paginated/trimmed list
  // serializer that may not include the full `message` body (a common
  // backend pattern: list = preview fields only, detail = full fields).
  // To make sure the message content set in AnnouncementModal actually
  // shows up here, this fetches the single notification's full detail by
  // id and merges it over the row data — falling back to the row itself
  // if that request fails, so the modal still opens with whatever we have.
  const handleNotificationClick = async (notif) => {
    if (!notif.is_read) {
      handleMarkRead(notif.id);
    }

    setSelectedNotif(notif); // open immediately with what we have
    setDetailLoading(true);
    try {
      const res = await api.get(`notifications/${notif.id}/`);
      const full = res.data?.data || res.data;
      if (full) {
        setSelectedNotif((prev) => (prev && prev.id === notif.id ? { ...notif, ...full } : prev));
      }
    } catch {
      // Detail fetch failed — keep showing the row data already set above.
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAnnouncementSent = (recipientCount) => {
    toast.success(`Announcement sent to ${recipientCount} user(s)!`);
    setShowCreateModal(false);
    fetchNotifications();
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div className="container">
      {/* Page Header — same structure/classes as the Employee page */}
      <div className="page-header">
        <h2> Notifications</h2>
        <div style={{ display: "flex", gap: 10 }}>
          {canCreate && (
            <button className="primary-btn" onClick={() => setShowCreateModal(true)}>
              <FaBullhorn style={{ marginRight: 6 }} /> Send Announcement
            </button>
          )}
          <button className="primary-btn btn-theme-accent" onClick={handleMarkAllRead}>
            <FaCheckDouble style={{ marginRight: 6 }} /> Mark All Read
          </button>
        </div>
      </div>

      {/* Tabs — same account-tabs/account-tab classes and inline-icon
          pattern as the Employee page's Directory / Org Chart tabs */}
      <div className="account-tabs" style={{ marginBottom: 24 }}>
        {TAB_OPTIONS.map((tab) => (
          <button
            key={tab.value}
            className={`account-tab ${activeTab === tab.value ? "active" : ""}`}
            onClick={() => setActiveTab(tab.value)}
          >
            {tab.icon}  {tab.label}
          </button>
        ))}
      </div>

      {/* Filters — same filters-card + Dropdown pattern as Employee */}
      <div className="filters-card single">
        <Dropdown
          options={TYPE_FILTER_OPTIONS}
          value={typeFilter}
          onChange={setTypeFilter}
          placeholder="Type"
        />
        <Dropdown
          options={PRIORITY_FILTER_OPTIONS}
          value={priorityFilter}
          onChange={setPriorityFilter}
          placeholder="Priority"
        />
      </div>

      {/* Notification List Container */}
      <div className="notif-page-card">
        {loading ? (
          <div className="empty-state">
            <p>Loading Notifications…</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="empty-state">
            <img src="/images/notification.svg" alt="No notification" className="empty-img" />
            <p>No Notifications Found</p>
          </div>
        ) : (
          <div className="notif-page-list">
            {notifications.map((n) => (
              <div
                key={n.id}
                className={`notif-page-item ${n.is_read ? "read" : "unread"}`}
                onClick={() => handleNotificationClick(n)}
              >
                <div className="notif-left">
                  <span className="unread-indicator" />
                  {getTypeIcon(n.notification_type)}
                  <div className="notif-content">
                    <div className="notif-header-row">
                      <span className="notif-title-text">{n.title}</span>
                      <span className={getPriorityBadgeClass(n.priority)}>
                        {n.priority_display}
                      </span>
                    </div>
                    {n.message && <p className="notif-message-text">{n.message}</p>}
                    <div className="notif-meta">
                      {n.sender_name && <span>From: {n.sender_name} • </span>}
                      <span>{formatRelativeTime(n.created_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="notif-actions">
                  {!n.is_read && (
                    <button
                       className="icon-btn view"
                      style={{ color: "#28a745" }}
                      title="Mark as read"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkRead(n.id);
                      }}
                    >
                      <FaCheck />
                    </button>
                  )}
                  <button
                    className="icon-btn delete"
                    title="Delete notification"
                    onClick={(e) => handleDeleteClick(n, e)}
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Pagination page={page} totalPages={totalPages} setPage={setPage} />
      </div>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <ConfirmModal
          title="Delete Notification"
          message={`Are you sure you want to delete "${deleteTarget.title}"?`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          requirePassword={false}
          onConfirm={confirmDeleteNotification}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* View section */}
      {selectedNotif && (
        <NotificationDetailModal
          notif={selectedNotif}
          loading={detailLoading}
          onClose={() => setSelectedNotif(null)}
        />
      )}

      {/* Announcement section */}
      {showCreateModal && (
        <AnnouncementModal onClose={() => setShowCreateModal(false)} onSent={handleAnnouncementSent} />
      )}
    </div>
  );
}